class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      const cartDrawerItems = this.querySelector('cart-drawer-items');
      if (cartDrawerItems) {
        try {
          if (typeof cartDrawerItems.updateCartUpsellToggleState === 'function') {
            cartDrawerItems.updateCartUpsellToggleState();
          }
          if (typeof cartDrawerItems.updateCartUpsellVisibility === 'function') {
            cartDrawerItems.updateCartUpsellVisibility();
          }
        } catch (e) {
          // Never let an upsell-toggle hiccup block the drawer from finishing its open sequence.
        }
      }
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    try {
      this.querySelector('.drawer__inner').classList.contains('is-empty') &&
        this.querySelector('.drawer__inner').classList.remove('is-empty');
    } catch (e) {
      console.error('[ruvr cart-drawer] is-empty toggle failed', e);
    }

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      try {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        if (!sectionElement) {
          console.error('[ruvr cart-drawer] target element missing for section', section.id, section.selector);
          return;
        }
        const html = parsedState.sections && parsedState.sections[section.id];
        if (!html) {
          console.error('[ruvr cart-drawer] no html in response for section', section.id);
          return;
        }
        const inner = this.getSectionInnerHTML(html, section.selector);
        if (inner == null) return;
        sectionElement.innerHTML = inner;
      } catch (e) {
        console.error('[ruvr cart-drawer] failed to update section', section.id, e);
      }
    });

    // Keep "You might like" in sync with this same response instead of waiting on
    // cart-upsell.js's separate 250ms-debounced full-page refetch, which was racing
    // against open() below and could leave the drawer showing a half-updated state
    // on the very first add-to-cart in a session.
    try {
      const freshCartDrawerHtml = parsedState.sections && parsedState.sections['cart-drawer'];
      if (freshCartDrawerHtml) {
        const freshUpsellWrapper = this.getSectionDOM(freshCartDrawerHtml, '#cart-drawer-upsell-wrapper');
        const currentUpsellWrapper = document.querySelector('#cart-drawer-upsell-wrapper');
        if (freshUpsellWrapper && currentUpsellWrapper) {
          currentUpsellWrapper.replaceWith(freshUpsellWrapper);
        }
      }
      if (typeof window.ruvrInsertCartDrawerUpsell === 'function') {
        window.ruvrInsertCartDrawerUpsell();
      }
    } catch (e) {
      console.error('[ruvr cart-drawer] upsell sync failed', e);
    }

    // FIX: right after the swap, something leaves .drawer__header at display:none and
    // <cart-drawer-items> (plus its nested wrapper/table) at collapsed/default display values,
    // making the drawer look like content is missing even though it's fully present. Force the
    // correct display explicitly at every level so no external rule (even !important) can hide them.
    try {
      const headerEl = this.querySelector('.drawer__header');
      if (headerEl) headerEl.style.setProperty('display', 'block', 'important');

      const itemsEl = this.querySelector('cart-drawer-items');
      if (itemsEl) itemsEl.style.setProperty('display', 'block', 'important');

      const formEl = this.querySelector('#CartDrawer-Form');
      if (formEl) formEl.style.setProperty('display', 'block', 'important');

      const contentsEl = this.querySelector('#CartDrawer-CartItems');
      if (contentsEl) contentsEl.style.setProperty('display', 'block', 'important');

      const wrapperEl = this.querySelector('.drawer__cart-items-wrapper');
      if (wrapperEl) wrapperEl.style.setProperty('display', 'block', 'important');

      const tableEl = this.querySelector('table.cart-items');
      if (tableEl) tableEl.style.setProperty('display', 'table', 'important');

      this.querySelectorAll('.cart-item').forEach((row) => {
        row.style.setProperty('display', 'table-row', 'important');
      });
    } catch (e) {
      console.error('[ruvr cart-drawer] display fix failed', e);
    }

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    if (!el) {
      console.error('[ruvr cart-drawer] selector not found in section response:', selector);
      return null;
    }
    return el.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  // CartDrawer.renderContents() already updates this drawer's content directly from
  // the single /cart/add.js (or /cart/change.js) response. The base CartItems.onCartUpdate()
  // instead does a SEPARATE fetch to /cart?section_id=cart-drawer and does a global
  // document.querySelector + replaceWith on cart-drawer-items and .cart-drawer__footer.
  // That second, independently-timed update was racing against renderContents() and
  // could land first/out of order, leaving the drawer showing an incomplete swap
  // (header and items missing) right after add-to-cart. Overriding it to a no-op
  // (aside from the harmless upsell-toggle sync) removes that race entirely.
  onCartUpdate() {
    this.updateCartUpsellToggleState();
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
